import { FunctionComponent, useMemo, useEffect, useRef } from "react";
import './styles/DashboardPage.scss';
import {
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";

import { Box, Stack } from "@mui/material";

import { useScreenSize, useSignOut, useAuth, useAppSettings, useBinaryToggle } from "@dw/hooks";
import {
  DashboardTopNavigationBar,
  DashboardSideNavigationBar,
  INavMenuItem,
} from "@dw/components/dashboard";
import { IMenuItem } from "@dw/models/shared";
import { APP_TABS_LABELS, MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB, MANAGE_DANCE_SCHOOL_LOCATION_TAB, MANAGE_DANCE_SCHOOL_STUDENTS_TAB, MANAGE_DANCE_SCHOOL_TAB, ROUTES, SCREEN_SIZE } from "@dw/constants";
import { buildSupportEmail } from "@dw/utils/supportUtils";
import { AppVersionChecker } from "@dw/AppVersionChecker";
import { DanceSchoolSwitcherModal } from "@dw/components/dance-schools";
import { formatRoute, compareToRoutePattern, componentClassName, classNames } from "@dw/utils";
import { APP_ICONS } from "@dw/constants/appIcons";
import { USER_ROLE } from "@dw/models/danceSchoolUser";
import { useUnreadTeacherNotesCount } from "@dw/hooks/api/TeacherNotesApi";

const cn = componentClassName('dashboard-page');

export const getScrollingElementClassName = () => cn('child-page-wrapper');

const DashboardPage: FunctionComponent = () => {

  const { currentSize } = useScreenSize();
  const { signOut, isSigningOut } = useSignOut();
  const { user, userHasRole } = useAuth();
  const { appSettings } = useAppSettings();

  const isAdmin = userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN);
  const unreadTeacherNotesCount = useUnreadTeacherNotesCount();

  const location = useLocation();
  const navigate = useNavigate();

  const childPageWrapperRef = useRef<HTMLDivElement>(null);

  const { isOpen: switchDanceSchoolOpen, toggle: toggleSwitchDanceSchoolOpen } = useBinaryToggle();

  const supportEmailDetails = useMemo(() => buildSupportEmail(appSettings, user), [appSettings, user]);

  const navMenuItems = useMemo<INavMenuItem[]>(() => {
    return [
      {
        text: "My School",
        routerLink: ROUTES.ManageDanceSchool,
        icon: APP_ICONS.SCHOOL,
        showChildItems: true,
        isActive: compareToRoutePattern(ROUTES.ManageDanceSchool, location.pathname),
        childItems: [
          {
            icon: APP_ICONS.LOCATIONS,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.LOCATIONS],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.LOCATIONS }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.LOCATIONS },
            showChildItems: compareToRoutePattern(ROUTES.ManageLocation, location.pathname),
            isActive: compareToRoutePattern(ROUTES.ManageLocation, location.pathname),
            childItems: [
              {
                icon: APP_ICONS.SCHEDULE,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_LOCATION_TAB.SCHEDULE],
                routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_LOCATION_TAB.SCHEDULE }
              },
              {
                icon: APP_ICONS.STUDENTS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDENTS],
                routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDENTS }
              },
              {
                icon: APP_ICONS.CLASSES,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_LOCATION_TAB.CLASSES],
                routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_LOCATION_TAB.CLASSES }
              },
              {
                icon: APP_ICONS.STUDIOS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDIOS],
                routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDIOS }
              }
            ]
          },
          {
            icon: APP_ICONS.STUDENTS,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.STUDENTS],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS },
            showChildItems: true,
            isActive: [
              compareToRoutePattern(ROUTES.ManageStudent, location.pathname),
              compareToRoutePattern(ROUTES.ViewDancerAccount, location.pathname),
              compareToRoutePattern(ROUTES.RegisterTrialClass, location.pathname),
            ].some(x => x),
            childItems: [
              {
                icon: APP_ICONS.STUDENTS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_STUDENTS_TAB.STUDENT_LIST],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS, subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.STUDENT_LIST }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.STUDENT_LIST },
                isActive: compareToRoutePattern(ROUTES.ManageStudent, location.pathname)
              },
              {
                icon: APP_ICONS.ENROLMENTS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ENROLMENTS],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS, subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ENROLMENTS }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ENROLMENTS },
                hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN)
              },
              {
                icon: APP_ICONS.TRIALS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_STUDENTS_TAB.TRIALS],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS, subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.TRIALS }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.TRIALS },
                hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN),
                isActive: compareToRoutePattern(ROUTES.RegisterTrialClass, location.pathname)
              },
              {
                icon: APP_ICONS.ACCOUNTS,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ACCOUNTS],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS, subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ACCOUNTS }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ACCOUNTS },
                hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN),
                isActive: compareToRoutePattern(ROUTES.ViewDancerAccount, location.pathname)
              },
              {
                icon: APP_ICONS.INVOICES,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_STUDENTS_TAB.INVOICES],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STUDENTS, subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.INVOICES }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_STUDENTS_TAB.INVOICES },
                hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN),
                isActive: compareToRoutePattern(ROUTES.ViewDancerAccount, location.pathname)
              },
            ]
          },
          {
            icon: APP_ICONS.COMMUNICATION,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION },
            badgeValue: unreadTeacherNotesCount || undefined,
            badgeColor: 'error',
            showChildItems: isAdmin,
            childItems: isAdmin ? [
              {
                icon: APP_ICONS.COMMUNICATION,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.MESSAGES],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION, subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.MESSAGES }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.MESSAGES }
              },
              {
                icon: APP_ICONS.TEACHER_NOTES,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.TEACHER_NOTES],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION, subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.TEACHER_NOTES }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.TEACHER_NOTES },
                // The parent Communication item carries the same count; repeating it here shows
                // which drill-down the number comes from.
                badgeValue: unreadTeacherNotesCount || undefined,
                badgeColor: 'error'
              },
              {
                icon: APP_ICONS.NOTICE_BOARD,
                text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.NOTICE_BOARD],
                routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION, subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.NOTICE_BOARD }),
                routerQueryParams: { subTab: MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.NOTICE_BOARD }
              }
            ] : undefined
          },
          {
            icon: APP_ICONS.EXAMS,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.EXAMS],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.EXAMS }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.EXAMS },
            // SAME role set as the tab entry in ManageDanceSchoolPage - these two lists have
            // already drifted once (Business Metrics). Keep them identical.
            hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN),
            isActive: [
              compareToRoutePattern(ROUTES.ManageExam, location.pathname),
              compareToRoutePattern(ROUTES.ManageExamClass, location.pathname)
            ].some(x => x)
          },
          {
            icon: APP_ICONS.METRICS,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.BUSINESS_METRICS],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.BUSINESS_METRICS }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.BUSINESS_METRICS },
            hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN)
          },
          {
            icon: APP_ICONS.STAFF,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.STAFF],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.STAFF }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.STAFF },
            hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN)
          },
          {
            icon: APP_ICONS.SETTINGS,
            text: APP_TABS_LABELS[MANAGE_DANCE_SCHOOL_TAB.SETTINGS],
            routerLink: formatRoute(ROUTES.ManageDanceSchool, {}, { tab: MANAGE_DANCE_SCHOOL_TAB.SETTINGS }),
            routerQueryParams: { tab: MANAGE_DANCE_SCHOOL_TAB.SETTINGS },
            hidden: !userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN)
          }
        ]
      },
      { text: "My Profile", routerLink: ROUTES.ManageMyProfile, icon: APP_ICONS.MY_PROFILE },
      { isDivider: true },
      { text: "Support", value: "SUPPORT_EMAIL", icon: APP_ICONS.SUPPORT },
      { text: "Sign Out", value: "SIGN_OUT", icon: APP_ICONS.SIGN_OUT },
    ];
  }, [location, userHasRole, isAdmin, unreadTeacherNotesCount]);

  const handleMenuItemSelected = (menuItem: IMenuItem) => {
    switch (menuItem.value) {
      case "SIGN_OUT":
        signOut();
        break;
      case "SUPPORT_EMAIL":
        window.open(supportEmailDetails.href);
        break;
      case "SWITCH":
        toggleSwitchDanceSchoolOpen();
        break;
    }
  };

  useEffect(() => {
    const pathName = location.pathname;
    if (pathName.endsWith(ROUTES.Dashboard) || pathName.endsWith(`${ROUTES.Dashboard}/`)) {
      navigate(ROUTES.ManageDanceSchool);
    }

    if (childPageWrapperRef.current) {
      childPageWrapperRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <Stack
      direction="column"
      className={cn()}
    >
      <DashboardTopNavigationBar
        menuItems={navMenuItems}
        menuItemSelected={handleMenuItemSelected}
        isLoading={isSigningOut}
      />

      <Stack
        direction="row"
        flex={1}
        className={cn('main-wrapper')}
      >
        {currentSize > SCREEN_SIZE.SM && (
          <DashboardSideNavigationBar
            menuItems={navMenuItems}
            menuItemSelected={handleMenuItemSelected}
          />
        )}
        <Box
          flex={1}
          ref={childPageWrapperRef}
          className={classNames(getScrollingElementClassName(), {
            [cn('child-page-wrapper', 'xs')]: currentSize === SCREEN_SIZE.XS,
            [cn('child-page-wrapper', 'sm')]: currentSize === SCREEN_SIZE.SM,
            [cn('child-page-wrapper', 'md')]: currentSize === SCREEN_SIZE.MD,
            [cn('child-page-wrapper', 'lg')]: currentSize === SCREEN_SIZE.LG
          })}
        >
          <Outlet />
        </Box>
      </Stack>

      <AppVersionChecker />

      <DanceSchoolSwitcherModal
          open={switchDanceSchoolOpen}
          onClose={toggleSwitchDanceSchoolOpen}
      />
    </Stack>
  );
};

export default DashboardPage;
